<template>
  <div>
    <div v-if="displayType == 'small'">
      <Button :color="'light'" :size="'small'" :type="'button'" @click="openModal()">
        <slot v-if="useSlot" :name="value[label]" />
        <span v-else>
          {{ value[label] }}
        </span>
        <span class="_button-suffix_1t1tt_13">
          <svg
            class="svg-component"
            fill="none"
            height="16"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 9L11.2191 14.3306C11.6684 14.7158 12.3316 14.7158 12.7809 14.3306L19 9"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-width="1.5"
            ></path>
          </svg>
        </span>
      </Button>
    </div>
    <AbanInput
      v-if="displayType == 'input'"
      :ref="'inputComponent'"
      :editable="!disabled"
      :model-value="
        multiSelect ? (multiValues.length > 0 ? 'no' : '') : value != null ? value[label] : ''
      "
      :placeholder="placeholder"
      :readonly="true"
      :show-content-slot="multiSelect || showContentSlot"
      :show-slot="true"
      :size="size"
      :type="'text'"
      :validations="inputValidations"
      @click="openModal()"
    >
      <template v-slot:content>
        <div
          v-for="(option, index) in $filters.uniqueByField(
            (multiSelect ? multiValues : [value]).concat(options),
            reduce
          )"
          :key="reduce(option)"
        >
          <slot v-if="isOptionSelected(option)" :name="reduce(option) + 'contentSelected'" />
        </div>
      </template>
      <svg
        aria-hidden="true"
        class="css-8mmkcg"
        focusable="false"
        height="20"
        viewBox="0 0 20 20"
        width="20"
      >
        <path
          d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
        ></path>
      </svg>
    </AbanInput>
    <slot v-if="displayType == 'custom'" :name="'display'" />
    <component
      :is="!noOptionsModal ? 'Modal' : 'div'"
      v-model:modal="modal"
      v-bind="finalModalOptions"
    >
      <ListOptions
        v-if="doSearch || doFilter"
        :ref="'listOption'"
        :filters="filters"
        :loading="listLoading"
        :no-filter="!doFilter"
        :no-search="!doSearch"
        :options="listOptions"
        :no-apply-button="noApplyButton"
        style="margin-bottom: 1rem"
        @list="list"
      />
      <ScrollPagination
        :empty-title="emptyTitle"
        :fixed-height="doSearch ? '30rem' : null"
        :length="retreivedOptions.length"
        :loading="listLoading > 0 || scrollLoading > 0"
        :max-height="maxHeight"
        :no-space="noSpace"
        :no-title-show="true"
        :title="title"
        @load-more="loadMore()"
      >
        <template v-for="(option, index) in retreivedOptions" :key="index" v-slot:[`${index}`]>
          <div
            :class="
              '_radio-option_bu0x4_18' +
              [isDisabled(option) ? '_disabled' : isOptionSelected(option) ? '_selected' : '']
            "
            role="radio"
            @click="
              () => {
                if (isDisabled(option)) return
                userSelected(option)
              }
            "
          >
            <slot v-if="useNotWrappedSlot" :name="reduce(option)" />
            <div
              v-else
              :class="
                '_radio-item_bu0x4_1 ' +
                [isOptionSelected(option) ? '_radio-item-checked_bu0x4_9 ' : '']
              "
            >
              <slot v-if="useSlot" :name="reduce(option)" />
              <span v-else>
                {{ option[label] }}
              </span>
              <svg
                v-if="isOptionSelected(option)"
                class="svg-component"
                fill="none"
                height="24"
                viewBox="0 0 24 24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 6L9 17L4 12"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="1.5"
                ></path>
              </svg>
            </div>
          </div>
        </template>
      </ScrollPagination>
    </component>
  </div>
</template>

<script>
import AbanInput from '../input/Input.vue'
import { FieldIsRequired } from '../../logic/validations/validations'
import Button from '../buttons/AbanButton.vue'
import Modal from '../Modal.vue'
import Loading from '../Loading.vue'
import { defineAsyncComponent } from 'vue'
import ScrollPagination from '../ScrollPagination.vue'

export default {
  name: 'AbanSelect',
  props: {
    modelValue: {
      default: null
    },
    multiModelValues: {
      default: []
    },
    reduced: {
      default: null
    },
    multiReduceds: {
      default: []
    },
    field: {
      required: false,
      default: 'id'
    },
    options: {
      type: Array,
      default: []
    },
    listOptions: {
      type: Object,
      default: {}
    },
    disabled: {
      type: Boolean
    },
    label: {
      required: false,
      default: 'id'
    },
    placeholder: {},
    title: {
      default: ''
    },
    size: {
      default: 'medium'
    },
    displayType: {
      default: 'input'
    },
    useSlot: {
      default: false
    },
    useNotWrappedSlot: {
      default: false
    },
    popupModal: {
      default: false
    },
    multiSelect: {
      default: false
    },
    disableSelect: {
      default: false
    },
    validations: {
      default: [new FieldIsRequired()]
    },
    doSearch: {
      default: false
    },
    doFilter: {
      default: false
    },
    noApplyButton: {
      default: false
    },
    doList: {
      default: false
    },
    filters: {
      default: []
    },
    listApiFunc: {
      required: false
    },
    nullable: {
      default: false
    },
    showContentSlot: {
      default: false
    },
    noOptionsModal: {
      default: false
    },
    emptyTitle: {
      default: ''
    },
    scrollLoading: {
      default: false
    },
    maxHeight: {
      default: '80vh'
    },
    selectSize: {
      default: 3
    },
    focusOnSearch: {
      default: true
    },
    noSpace: {
      default: false
    },
    modalOptions: {
      default: {
        noClose: false
      }
    },
    disabledFunc: {
      default: null
    }
  },
  components: {
    ScrollPagination,
    AbanInput,
    Button,
    Modal,
    Loading,
    ListOptions: defineAsyncComponent(() => import('../../components/list/ListOptions.vue'))
  },
  created() {
    this.retreivedOptions = this.options
    this.list(this.listOptions)
    this.updateByProps()
    this.inputValidations = this.validations
    this.modal = this.popupModal
  },
  data() {
    return {
      timer: null,
      value: null,
      multiValues: [],
      modal: false,
      listLoading: false,
      inputValidations: [],
      retreivedOptions: [],
      latestListOptions: null
    }
  },
  emits: [
    'update:modelValue',
    'update:multiModelValues',
    'update:reduced',
    'update:multiReduceds',
    'update:popupModal',
    'update:options',
    'update:listOptions',
    'selected',
    'list',
    'load-more'
  ],
  watch: {
    modal: function () {
      this.$emit('update:popupModal', this.modal)
    },
    popupModal: function () {
      this.modal = this.popupModal
      if (this.modal) this.openModal()
    },
    modelValue: {
      deep: true,
      handler: function () {
        this.updateByProps()
      }
    },
    multiModelValues: {
      deep: true,
      handler: function () {
        this.updateByProps()
      }
    },
    reduced: {
      deep: true,
      handler: function () {
        this.updateByProps()
      }
    },
    multiReduceds: {
      deep: true,
      handler: function () {
        this.updateByProps()
      }
    },
    value: {
      deep: true,
      handler: function () {
        this.$emit('update:modelValue', this.value)
        this.$emit('update:reduced', this.reduce(this.value))
      }
    },
    multiValues: {
      deep: true,
      handler: function () {
        this.$emit('update:multiModelValues', this.multiValues)
        if (this.multiValues != null) {
          this.$emit(
            'update:multiReduceds',
            this.multiValues.map((option) => this.reduce(option))
          )
        }
      }
    },
    options: {
      deep: true,
      handler: function () {
        this.retreivedOptions = this.options
        this.updateByProps()
      }
    },
    retreivedOptions: {
      deep: true,
      handler: function () {
        this.$emit('update:options', this.retreivedOptions)
        this.updateByProps()
      }
    },
    listOptions: {
      // deep: true,
      handler: function (old, ne) {
        this.list(this.$filters.removeNullAndEmptyValues(this.listOptions))
      }
    },
    latestListOptions: {
      // deep: true,
      handler: function () {
        this.$emit('update:listOptions', this.latestListOptions)
      }
    }
  },
  methods: {
    updateByProps() {
      if (this.multiSelect) this.updateByPropsMulti()
      else this.updateByPropsSingle()
    },
    updateByPropsSingle() {
      if (this.modelValue != null) this.value = this.modelValue
      else if (this.reduced != null)
        for (let option of this.retreivedOptions) {
          if (this.reduce(option) == this.reduced) this.select(option)
        }
    },
    updateByPropsMulti() {
      if (this.multiModelValues != null) this.multiValues = this.multiModelValues
      else if (this.multiReduceds != null) {
        this.multiValues = []
        for (let option of this.retreivedOptions)
          if (this.multiReduceds.includes(this.reduce(option))) this.select(option)
      }
    },
    userSelected(option) {
      if (this.disabled) return
      if (this.isOptionSelected(option) && (this.nullable || this.multiSelect)) {
        this.deselect(option)
        return
      }
      this.select(option)
      if (!this.multiSelect) this.closeModal()
    },
    select(option) {
      if (this.disableSelect) return
      if (this.multiSelect) this.selectMulti(option)
      else this.selectSingle(option)
      this.$emit('selected', option)
    },
    selectMulti(option) {
      this.multiValues.push(option)
    },
    selectSingle(option) {
      this.value = option
    },
    deselect(option) {
      if (!this.multiSelect) this.deselectSingle(option)
      else this.deselectMulti(option)
    },
    deselectSingle(option) {
      this.value = null
    },
    deselectMulti(option) {
      let reduceFunc = this.reduce
      this.multiValues = this.multiValues.filter(function (_option) {
        return reduceFunc(_option) != reduceFunc(option)
      })
    },
    isOptionSelected(option) {
      if (this.multiSelect) return this.isOptionSelectedMulti(option)
      else return this.isOptionSelectedSingle(option)
    },
    isOptionSelectedMulti(option) {
      if (this.multiValues == null) return false
      for (let selectedOption of this.multiValues)
        if (this.reduce(option) == this.reduce(selectedOption)) return true
      return false
    },
    isOptionSelectedSingle(option) {
      return this.reduce(option) == this.reduce(this.value)
    },
    reduce(x) {
      if (typeof x == 'object' && x != null)
        if (Object.keys(x).includes(this.field)) return x[this.field]
      return null
    },
    openModal() {
      if (this.disabled) return
      this.modal = true
      if (this.doSearch && this.focusOnSearch) {
        setTimeout(() => {
          this.$refs['listOption'].$refs['searchComponent'].$refs['searchInput'].focusOnInput()
        }, 100)
      }
    },
    closeModal() {
      this.modal = false
    },
    isValid() {
      if (Object.keys(this.$refs).length == 0) return
      return this.$refs['inputComponent'].isValid()
    },
    list(listOptions) {
      if (!this.doSearch && !this.doFilter && !this.doList) return
      if (JSON.stringify(this.latestListOptions) == JSON.stringify(listOptions)) return
      this.latestListOptions = JSON.parse(JSON.stringify(listOptions))
      this.listLoading += 1
      this.retreivedOptions = []
      this.listApiFunc(1, this.selectSize, listOptions)
        .then((options) => {
          this.retreivedOptions = options
        })
        .catch(() => {})
        .finally(() => {
          this.listLoading -= 1
        })
    },
    loadMore() {
      this.$emit('load-more')
    },
    isDisabled(transferRequest) {
      return this.disabledFunc != null && this.disabledFunc(transferRequest)
    }
  },
  computed: {
    finalModalOptions() {
      return {
        title: this.title,
        onlyChangeDisplay: this.modalOptions.onlyChangeDisplay ?? true,
        noClose: this.modalOptions.noClose
      }
    }
  }
}
</script>
